import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  DialogContent
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ContentCardProps } from './types'
import Close from '../../../../Spa.Core/components/_svg/Close'
import ThumbnailWithPlayButton from '../../blocks/VideoBlock/ThumbnailWithPlayButton'
import ReactPlayer from 'react-player'
import { useMediaQuery } from '@material-ui/core'

type Props = {
  item: ContentCardProps
  index?: number
  cardContainerClass?: string
  childrenBackgroundColor?: number
}

const ContentCard = ({
  item,
  index,
  cardContainerClass,
  childrenBackgroundColor
}: Props) => {
  const [open, setOpen] = useState(false)
  const [imageError, setImageError] = useState(false)
  const classes = useStyles()
  const isMobile = useMediaQuery('(max-width: 600px)')

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleImageError = () => {
    console.warn('ContentCard: Failed to load image:', item.teaserImageUrl)
    setImageError(true)
  }

  // Reset image error when image URL changes
  useEffect(() => {
    setImageError(false)
  }, [item.teaserImageUrl])

  return (
    <Box
      key={index}
      className={`${cardContainerClass}`}
      style={{
        backgroundColor: childrenBackgroundColor === 0 ? '#ffffff' : '#FAFAFA'
      }}
      height="460px"
    >
      {item.teaserImageUrl && !item.socialMediaUrl && !imageError && (
        <img 
          src={item.teaserImageUrl} 
          className={`${classes.mediaBase} ${classes.image}`}
          onError={handleImageError}
          alt={item.teaserAltText || item.teaserTitle || 'Content image'}
        />
      )}
      {item.socialMediaUrl && (
        <Box className={classes.mediaBase} style={{ position: 'relative' }}>
          <ThumbnailWithPlayButton
            handleStart={handleToggle}
            contentCardPlayButtonText={item.playButtonText}
            contentCardSocialUrl={item.socialMediaUrl}
            thumbnailUrl={item.thumbnailUrl}
          />
        </Box>
      )}
      {(!item.teaserImageUrl || imageError) && !item.socialMediaUrl && (
        <Box className={`${classes.mediaBase} ${classes.placeholder}`}>
          <Typography variant="body2" className={classes.placeholderText}>
            {imageError ? 'Image unavailable' : 'No image added'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={open}
        onClose={handleToggle}
        fullWidth
        className={classes.dialog}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={handleToggle} className={classes.close}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <ReactPlayer
            url={item.socialMediaUrl || ''}
            playing={true}
            controls={true}
          />
        </DialogContent>
      </Dialog>
      <Box className={classes.contentContainer}>
        {item.teaserTag && (
          <Box className={classes.badge}>{item.teaserTag}</Box>
        )}
        <Box>
          <Typography component="h4" className={classes.title}>
            {item.teaserTitle}
          </Typography>
        </Box>
        <Box className={classes.description}>
          <Typography component="p">{item.teaserText}</Typography>
        </Box>
        <Box className={classes.readMore}>
          <Typography component="p">
            <a href={item.teaserTargetUrl}>{item.teaserLinkText}</a>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles(({ breakpoints }) => ({
  mediaBase: {
    width: '100%',
    height: '200px',
    borderTopLeftRadius: '2px',
    borderTopRightRadius: '2px'
  },
  image: {
    objectFit: 'cover'
  },
  contentContainer: {
    padding: '16px',
    borderRadius: '2px'
  },
  title: {
    textTransform: 'uppercase',
    color: '#000',
    fontSize: '18px',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    fontFamily: 'Oswald',
    marginBottom: '12px',
    height: '60px',
    [breakpoints.down('sm')]: {
      fontSize: '21px'
    }
  },
  badge: {
    borderRadius: '2px',
    border: '1px solid #C5C5C5',
    fontSize: '12px',
    lineHeight: '126%',
    padding: '4px 8px',
    display: 'inline-flex',
    marginBottom: '8px',
    [breakpoints.down('sm')]: {
      fontSize: '12px'
    }
  },
  description: {
    fontSize: '14px',
    lineHeight: '17px',
    marginBottom: '24px',
    height: '75px',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    [breakpoints.down('sm')]: {
      fontSize: '16px'
    }
  },
  readMore: {
    color: '#009DE0',
    borderBottom: '1px solid #009DE0',
    display: 'inline-flex',
    position: 'relative',
    '& a': {
      fontSize: '14px',
      lineHeight: '120%',
      [breakpoints.down('sm')]: {
        fontSize: '16px'
      },
      '&:hover': {
        color: '#007AC0'
      }
    }
  },
  close: {
    justifyContent: 'flex-end',
    width: 'fit-content'
  },
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 'fit-content',
      [breakpoints.down('sm')]: {
        margin: '0px',
        width: '100%'
      },
      '& .MuiDialogContent-root': {
        padding: '16px',
        [breakpoints.down('sm')]: {
          padding: '0px',
          '& > div': {
            width: '100vw !important'
          }
        }
      }
    }
  },
  placeholder: {
    backgroundColor: '#F5F5F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #E0E0E0'
  },
  placeholderText: {
    color: '#9E9E9E',
    fontSize: '14px',
    fontStyle: 'italic'
  }
}))

export default ContentCard
